import { type ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import siteCss from "@quintara/site-kit/styles/site.css?raw";
import lightCss from "@quintara/site-kit/styles/light.css?raw";
import { WEBSITE_ORIGIN } from "../common/constants";

// An isolated iframe that loads the real site CSS, then React-portals the
// preview content into it. The iframe sandboxes the site's global styles from
// the admin chrome, so the preview is pixel-accurate without CSS bleed. A
// <base href> pointing at the live site resolves relative image URLs.
const FONTS =
  '<link rel="preconnect" href="https://fonts.googleapis.com">' +
  '<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>' +
  '<link href="https://fonts.googleapis.com/css2?family=Figtree:wght@400;500;600;700&family=JetBrains+Mono&family=Noto+Sans+Arabic&display=swap" rel="stylesheet">';

// Affordance for inline click-to-edit. Only matches when the admin renders
// editable fields (data-cms-editable + contenteditable), which never exist on
// the public site, so this is inert outside edit mode.
// NOTE: never set `background` here — gradient text (.grad-text) paints its
// gradient into `background` and clips it to the glyphs, so overriding it would
// make those letters invisible. Use box-shadow for the hover/focus fill instead.
const EDIT_CSS = `
[data-cms-editable][contenteditable]{outline:1px dashed rgba(123,98,224,.55);outline-offset:3px;border-radius:3px;cursor:text;caret-color:#7b62e0;transition:outline-color .15s,box-shadow .15s;}
[data-cms-editable][contenteditable]:hover{outline-color:#7b62e0;box-shadow:0 0 0 4px rgba(123,98,224,.08);}
[data-cms-editable][contenteditable]:focus{outline:2px solid #7b62e0;box-shadow:0 0 0 4px rgba(123,98,224,.12);}
a[data-cms-nav]{cursor:pointer;}
a[data-cms-nav]:hover{outline:2px dashed rgba(123,98,224,.5);outline-offset:3px;border-radius:5px;}
`;

export function PreviewFrame({ children }: { children: ReactNode }) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [mountNode, setMountNode] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const doc = frameRef.current?.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(
      `<!doctype html><html><head><meta charset="utf-8">` +
        `<base href="${WEBSITE_ORIGIN}/">` +
        FONTS +
        `<style>${siteCss}\n${lightCss}\n${EDIT_CSS}</style></head>` +
        `<body><div id="preview-root"></div></body></html>`,
    );
    doc.close();
    setMountNode(doc.getElementById("preview-root"));
  }, []);

  return (
    <iframe ref={frameRef} className="preview-frame" title="Live preview">
      {mountNode ? createPortal(children, mountNode) : null}
    </iframe>
  );
}
